/**
 * CENTRALIZED THEME & COLOR DEFINITIONS
 * Single source of truth for all colors used in the application.
 * Import these constants instead of hardcoding Tailwind classes or hex colors.
 * 
 * Update here ONLY - no need to search 25 files when changing colors.
 */

import type { CSSProperties } from 'react';
import type { CaseInternalStatus } from '../types.ts';

// ===== BRAND COLORS =====
export const BRAND = {
  primary: '#200b2b',           // Deep dark plum - main brand color
  primaryDark: '#2a063d',       // Darker plum for hover states & active menu
  primarySoft: '#f5e6e8',       // Very light plum for backgrounds
  accent: '#f6f3e4',            // Warm cream accent
} as const;

// ===== CREAM/YELLOW PALETTE (Base: #f6f3e4) =====
export const CREAM = {
  50: '#fef9f0',                // Very light shade (up)
  100: '#fdfcf8',               // Lighter shade (up)
  200: '#f6f3e4',               // Base cream color
  300: '#ede9d4',               // Shade down
  400: '#e6d487',               // Darker shade
  500: '#ddc770',               // Even darker
  600: '#d4b861',               // Darker text
  700: '#cba952',               // Darkest text
} as const;

// ===== SEMANTIC COLOR ALIASES =====
// Use these instead of hardcoding class names - semantic meaning is clear
export const COLORS = {
  // Primary brand (indigo override)
  primary: BRAND.primary,
  primaryHover: BRAND.primaryDark,
  primaryLight: BRAND.primarySoft,
  primaryAccent: BRAND.accent,

  // Neutrals
  white: '#ffffff',
  black: '#000000',
  
  // Status colors
  success: '#10b981',            // Emerald - success/complete
  successLight: '#d1fae5',       // Emerald light
  warning: CREAM[700],           // Cream dark - warning
  warningLight: CREAM[100],      // Cream light
  danger: '#ef4444',             // Red - error/delete
  dangerLight: '#fee2e2',        // Red light
  info: BRAND.primary,           // Plum - info/neutral

  // Text colors
  textPrimary: '#0f172a',        // Dark text
  textSecondary: '#64748b',      // Medium gray
  textTertiary: '#94a3b8',       // Light gray
  textDisabled: '#cbd5e1',       // Disabled gray

  // Backgrounds
  bgDefault: '#f8fafc',          // Default light bg
  bgCard: '#ffffff',             // Card white bg
  bgAlt: '#f1f5f9',              // Alternative light bg
  bgOverlay: 'rgba(15, 23, 42, 0.6)', // Dark overlay

  // Borders
  borderLight: '#e2e8f0',        // Light border
  borderDefault: '#cbd5e1',      // Default border
  borderDark: '#64748b',         // Dark border
} as const;

export const MATTER_STAGE_STYLES = {
  INTAKE: { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' },
  ONGOING: { backgroundColor: '#d1fae5', color: '#065f46', borderColor: '#6ee7b7' },
  ENDING_SOON: { backgroundColor: '#ffedd5', color: '#9a3412', borderColor: '#fdba74' },
  ENDED: { backgroundColor: '#ffe4e6', color: '#9f1239', borderColor: '#fda4af' },
  BILLING: { backgroundColor: '#ede9fe', color: '#5b21b6', borderColor: '#c4b5fd' },
  BILLED: { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' },
  INTERIM_POA: { backgroundColor: '#cffafe', color: '#155e75', borderColor: '#67e8f9' },
} as const satisfies Record<CaseInternalStatus, CSSProperties>;

const UNSPECIFIED_MATTER_STAGE_STYLE = {
  backgroundColor: COLORS.bgAlt,
  color: '#475569',
  borderColor: COLORS.borderDefault,
} as const;

export const getMatterStageStyle = (stage: string): CSSProperties => {
  const key = stage.trim().toUpperCase().replace(/\s+/g, '_');
  return Object.hasOwn(MATTER_STAGE_STYLES, key)
    ? MATTER_STAGE_STYLES[key as CaseInternalStatus]
    : UNSPECIFIED_MATTER_STAGE_STYLE;
};

// ===== TAILWIND CLASS TEMPLATES =====
// Pre-composed class strings for common patterns
export const CLASSES = {
  // Button styles
  buttonPrimary: 'bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-indigo-700 transition-all',
  buttonSecondary: 'bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-lg font-bold hover:bg-slate-50 transition-all',
  buttonDanger: 'bg-rose-50 border border-rose-100 text-rose-600 px-4 py-2 rounded-lg font-bold hover:bg-rose-100 transition-all',
  
  // Badge styles
  badgeSuccess: 'bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-full px-2 py-1 text-[9px] font-black uppercase',
  badgeWarning: 'bg-yellow-50 border border-yellow-100 text-yellow-600 rounded-full px-2 py-1 text-[9px] font-black uppercase',
  badgeDanger: 'bg-rose-50 border border-rose-100 text-rose-600 rounded-full px-2 py-1 text-[9px] font-black uppercase',
  badgeInfo: 'bg-indigo-50 border border-indigo-200 text-indigo-600 rounded-full px-2 py-1 text-[9px] font-black uppercase',

  // Input styles
  inputDefault: 'bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 transition-all',
  inputError: 'bg-white border border-rose-200 rounded-lg px-3 py-2 text-sm font-medium outline-none focus:ring-4 focus:ring-rose-500/10 focus:border-rose-600 transition-all',
  
  // Card styles
  cardDefault: 'bg-white rounded-2xl border border-slate-100 shadow-sm p-6',
  cardHover: 'bg-white rounded-2xl border border-slate-100 shadow-sm p-6 hover:shadow-md hover:-translate-y-1 transition-all',
  
  // Text sizes (with readability uplift)
  textXs: 'text-xs',             // 14px (uplifted from 12px)
  textSm: 'text-sm',             // 16px (uplifted from 14px)
  textBase: 'text-base',         // 16px
  textLg: 'text-lg',             // 18px
  textXl: 'text-xl',             // 20px
  
  // Status indicators
  statusSuccess: 'flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-semibold border border-emerald-200',
  statusWarning: 'flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 text-yellow-700 text-sm font-semibold border border-yellow-200',
  statusError: 'flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 text-rose-700 text-sm font-semibold border border-rose-200',
} as const;

// ===== COLOR UTILITIES =====
/**
 * Get a color with optional opacity
 * Usage: getColorWithOpacity(COLORS.primary, 0.5) => rgba(32, 11, 43, 0.5)
 */
export const getColorWithOpacity = (hexColor: string, opacity: number): string => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get Tailwind class string for a color value
 * Usage: getTailwindBgClass(CREAM[200]) => 'bg-yellow-200' (approximately)
 * Note: For precise control, use the hex colors directly in style props
 */
export const getTailwindTextColor = (semanticColor: keyof typeof COLORS): string => {
  const colorMap: Record<string, string> = {
    primary: 'text-indigo-600',
    primaryHover: 'text-indigo-700',
    primaryLight: 'text-indigo-100',
    success: 'text-emerald-600',
    successLight: 'text-emerald-100',
    warning: 'text-yellow-700',
    warningLight: 'text-yellow-100',
    danger: 'text-rose-600',
    dangerLight: 'text-rose-100',
    textPrimary: 'text-slate-900',
    textSecondary: 'text-slate-600',
    textTertiary: 'text-slate-500',
  };
  return colorMap[semanticColor] || 'text-slate-900';
};

export const getTailwindBgColor = (semanticColor: keyof typeof COLORS): string => {
  const colorMap: Record<string, string> = {
    primary: 'bg-indigo-600',
    primaryHover: 'bg-indigo-700',
    primaryLight: 'bg-indigo-50',
    primaryAccent: 'bg-yellow-100',
    success: 'bg-emerald-50',
    successLight: 'bg-emerald-100',
    warning: 'bg-yellow-50',
    warningLight: 'bg-yellow-100',
    danger: 'bg-rose-50',
    dangerLight: 'bg-rose-100',
    bgDefault: 'bg-slate-50',
    bgCard: 'bg-white',
  };
  return colorMap[semanticColor] || 'bg-white';
};

/**
 * Export all brand colors as CSS custom properties
 * Used in index.css via :root selector
 */
export const cssVariables = {
  '--brand-primary': BRAND.primary,
  '--brand-primary-dark': BRAND.primaryDark,
  '--brand-accent': BRAND.accent,
  '--brand-primary-soft': BRAND.primarySoft,
  '--brand-primary-mid': BRAND.accent,
  '--cream-50': CREAM[50],
  '--cream-100': CREAM[100],
  '--cream-200': CREAM[200],
  '--cream-300': CREAM[300],
  '--cream-400': CREAM[400],
  '--cream-500': CREAM[500],
  '--cream-600': CREAM[600],
  '--cream-700': CREAM[700],
} as const;
